/**
 * Относительное время в ленте уведомлений (ТЗ 0068).
 *
 * Своя таблица, а не `toLocaleString`: вывод локали браузера не совпадает с
 * языком интерфейса — человек с польским ERP на русской системе получил бы
 * смесь. Язык здесь приходит явным аргументом, тем же, что и у i18next.
 *
 * Функция чистая (принимает «сейчас» аргументом), поэтому проверяется без
 * подмены системных часов.
 */
export type TNotificationLocale = 'ru' | 'pl';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Русские числительные: «5 минут», но «21 минуту». */
const ruPlural = (
  n: number,
  one: string,
  few: string,
  many: string,
): string => {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  const mod10 = n % 10;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
};

/** Польские: 1 / 2-4 / 5+ с той же оговоркой про 12-14. */
const plPlural = (
  n: number,
  one: string,
  few: string,
  many: string,
): string => {
  if (n === 1) return one;
  const mod100 = n % 100;
  if (mod100 >= 12 && mod100 <= 14) return many;
  const mod10 = n % 10;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
};

const DICT = {
  ru: {
    now: 'только что',
    minutes: (n: number) =>
      `${n} ${ruPlural(n, 'минуту', 'минуты', 'минут')} назад`,
    hours: (n: number) => `${n} ${ruPlural(n, 'час', 'часа', 'часов')} назад`,
    yesterday: 'вчера',
    days: (n: number) => `${n} ${ruPlural(n, 'день', 'дня', 'дней')} назад`,
  },
  pl: {
    now: 'przed chwilą',
    minutes: (n: number) =>
      `${n} ${plPlural(n, 'minutę', 'minuty', 'minut')} temu`,
    hours: (n: number) =>
      `${n} ${plPlural(n, 'godzinę', 'godziny', 'godzin')} temu`,
    yesterday: 'wczoraj',
    days: (n: number) => `${n} ${plPlural(n, 'dzień', 'dni', 'dni')} temu`,
  },
} as const;

/**
 * «только что» / «12 минут назад» / «вчера» / «3 дня назад» / дата.
 *
 * Дальше недели показываем дату: «17 дней назад» человек всё равно переводит
 * в календарь, и лишний шаг ему не нужен.
 */
export const relativeTime = (
  isoOrDate: string | Date,
  locale: TNotificationLocale,
  now: Date = new Date(),
): string => {
  const at = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
  if (Number.isNaN(at.getTime())) return '';

  const dict = DICT[locale] ?? DICT.ru;
  const diff = now.getTime() - at.getTime();
  // Часы клиента могут отставать от серверных: время «из будущего» — это не
  // повод показывать «-3 минуты назад».
  if (diff < MINUTE) return dict.now;
  if (diff < HOUR) return dict.minutes(Math.floor(diff / MINUTE));
  if (diff < DAY) return dict.hours(Math.floor(diff / HOUR));

  const days = Math.floor(diff / DAY);
  if (days === 1) return dict.yesterday;
  if (days <= 7) return dict.days(days);

  return at.toLocaleDateString(locale === 'pl' ? 'pl-PL' : 'ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};
